import { anneeDe } from './cloture';
import { avoirsSur, soldeApresAvoirs } from './impayes';
import { jourIso } from './rapprochement-sorties';
import { auCentime, compteDansLeCa, ttcDocument } from './vat';
import { stockAuDebut, valeurDuStock } from './valeur-stock';

/**
 * Le bilan au 31 décembre, année par année.
 *
 * <p>La banque, la caisse et les dettes étaient trois cases uniques : le
 * solde tapé pour 2026 s'affichait aussi au bilan de 2025, et celui de 2025
 * disparaissait en tapant celui de 2026. Chaque exercice garde maintenant
 * ses soldes au 31.12, et les factures encore dues se comptent à cette date.
 */

/** Les trois soldes que l'application ne peut pas déduire. */
export const SOLDES_DU_BILAN = ['banque', 'caisse', 'dettes'];

const nombre = (v) => (v === undefined || v === null || v === '' ? null : +v);

/**
 * Les soldes au 31.12 d'un exercice : ceux notés pour cette année ; pour
 * l'année en cours, à défaut, ceux notés avant le classement par année.
 *
 * @param {object} [bilan] réglage du bilan ({soldes: {annee: {banque, caisse, dettes}}})
 * @param {number} annee exercice
 * @param {Date} [maintenant] aujourd'hui
 * @returns {{banque: number|null, caisse: number|null, dettes: number|null}} les soldes, null si pas notés
 */
export function soldesDeLAnnee(bilan, annee, maintenant = new Date()) {
  const notes = bilan?.soldes?.[annee] || {};
  const ancien = annee === maintenant.getFullYear() && !bilan?.soldes?.[annee];
  const sortie = {};
  for (const champ of SOLDES_DU_BILAN) {
    const v = nombre(notes[champ]);
    sortie[champ] = v !== null && Number.isFinite(v) ? v : ancien ? nombre(bilan?.[champ]) : null;
    if (sortie[champ] !== null && !Number.isFinite(sortie[champ])) sortie[champ] = null;
  }
  return sortie;
}

/**
 * Le réglage du bilan avec un solde noté pour une année.
 *
 * @param {object} [bilan] réglage du bilan
 * @param {number} annee exercice
 * @param {'banque'|'caisse'|'dettes'} champ solde
 * @param {string|number} valeur saisie (vide : effacé)
 * @param {Date} [maintenant] aujourd'hui
 * @returns {object} le réglage mis à jour
 */
export function noteLeSolde(bilan, annee, champ, valeur, maintenant = new Date()) {
  // La première saisie de l'année en cours reprend les autres soldes déjà
  // notés à l'ancienne : ils ne disparaissent pas de l'écran.
  const deja = bilan?.soldes?.[annee] || {};
  const depart = bilan?.soldes?.[annee]
    ? deja
    : Object.fromEntries(
        Object.entries(soldesDeLAnnee(bilan, annee, maintenant)).filter(([, v]) => v !== null),
      );
  return {
    ...(bilan || {}),
    soldes: {
      ...(bilan?.soldes || {}),
      [annee]: { ...depart, [champ]: valeur === '' ? '' : +valeur },
    },
  };
}

const finDeLAnnee = (annee) => annee + '-12-31';

/**
 * Ce que les clients devaient encore au 31.12 d'un exercice : les factures
 * datées jusqu'au 31.12, moins ce qu'ils avaient payé à cette date et les
 * avoirs émis jusque-là.
 *
 * <p>Seules les factures de l'année affichée, impayées aujourd'hui, étaient
 * comptées : une facture de décembre 2026 payée en janvier 2027 sortait du
 * bilan 2026, et une facture de 2025 toujours due n'entrait pas dans celui de
 * 2026.
 *
 * @param {object} p
 * @param {object[]} p.factures toutes les factures (avoirs compris)
 * @param {Map<number, {date: string, montant: number}[]>} [p.paiements] paiements inscrits par facture
 * @param {number} p.annee exercice
 * @param {Date} [p.maintenant] aujourd'hui
 * @returns {number} le montant dû au 31.12
 */
export function debiteursAu31Decembre({ factures, paiements, annee, maintenant = new Date() }) {
  const liste = factures || [];
  const fin = finDeLAnnee(annee);
  const jusquA = (d) => {
    const j = jourIso(d);
    return !!j && j <= fin;
  };
  // L'année en cours n'est pas finie : ce qui reste dû aujourd'hui.
  if (annee >= maintenant.getFullYear()) {
    return auCentime(
      liste
        .filter(
          (f) =>
            (f.statut === 'Envoyée' || f.statut === 'Partielle') &&
            f.type !== 'avoir' &&
            anneeDe(f, maintenant) <= annee,
        )
        .reduce((s, f) => s + soldeApresAvoirs(f, liste), 0),
    );
  }
  let total = 0;
  for (const f of liste) {
    if (f.type === 'avoir' || !compteDansLeCa(f) || f.statut === 'Brouillon') continue;
    if (anneeDe(f, maintenant) > annee) continue;
    const ttc = ttcDocument(f);
    if (!(ttc > 0)) continue;
    const inscrits = paiements?.get?.(f.id);
    let paye;
    if (inscrits?.length) {
      paye = inscrits.filter((p) => jusquA(p.date)).reduce((s, p) => s + (+p.montant || 0), 0);
    } else {
      const encaisse = f.statut === 'Payée' ? ttc : +f.paidAmount || 0;
      // Sans date de paiement, on ne sait pas : la facture est tenue pour
      // payée à temps, comme elle l'est aujourd'hui.
      paye = !f.paidDate || jusquA(f.paidDate) ? encaisse : 0;
    }
    const avoirs = avoirsSur(f, liste)
      .filter((a) => anneeDe(a, maintenant) <= annee)
      .reduce((s, a) => s + ttcDocument(a), 0);
    total += Math.max(0, ttc - paye - avoirs);
  }
  return auCentime(total);
}

/**
 * La valeur du stock au 31.12, au prix d'achat : celle de la clôture, sinon,
 * pour l'année en cours, le dépôt d'aujourd'hui, et pour une année passée
 * celle retrouvée depuis le journal du dépôt (stock au 1er janvier suivant).
 *
 * @param {object} p
 * @param {object[]} p.stock articles du dépôt aujourd'hui
 * @param {object} [p.clotures] clôtures par année
 * @param {number} p.annee exercice
 * @param {Date} [p.maintenant] aujourd'hui
 * @returns {number|null} la valeur, ou null sans dépôt connu
 */
export function stockAu31Decembre({ stock, clotures, annee, maintenant = new Date() }) {
  const c = clotures?.[annee];
  if (c && c.stock !== undefined && Number.isFinite(+c.stock)) return +c.stock;
  if (annee >= maintenant.getFullYear()) return valeurDuStock(stock).achat;
  return stockAuDebut({ clotures, stock, annee: annee + 1 });
}
